import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { StudentSection } from "./StudentLayout";
import { LogOut, ChevronRight, Bell, BellOff, HelpCircle, Building2, Check, Megaphone, Calendar, Award, Pencil, Plus } from "lucide-react";
import { enablePush, disablePush, getPushStatus, isPushSupported } from "@/lib/push";
import StudentProfileEditModal from "./StudentProfileEditModal";
import LanguagePreference from "@/components/LanguagePreference";

const CATS = [
  { key: "broadcasts", label: "Academy announcements", desc: "New posts from the academy", Icon: Megaphone },
  { key: "classes",    label: "Class reminders",       desc: "Reservation reminders & changes", Icon: Calendar },
  { key: "seminars",   label: "Seminars & events",     desc: "Special trainings & graduations", Icon: Award },
];

export default function StudentProfile() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [pushStatus, setPushStatus] = useState("default");
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState("");
  const [supported, setSupported] = useState(true);
  const [prefs, setPrefs] = useState({ broadcasts: true, classes: true, seminars: true });
  const [editing, setEditing] = useState(false);
  const [academies, setAcademies] = useState(null);
  const [showJoin, setShowJoin] = useState(false);
  const [busyAcademy, setBusyAcademy] = useState(null);
  const [academyError, setAcademyError] = useState("");

  const loadMe = useCallback(() => api.get("/student/me").then(r => setMe(r.data)), []);
  const loadAcademies = useCallback(() => api.get("/student/my-academies").then(r => setAcademies(r.data)).catch(() => {}), []);
  useEffect(() => {
    loadMe();
    loadAcademies();
    isPushSupported().then(setSupported);
    getPushStatus().then(setPushStatus);
    api.get("/student/notifications").then(r => setPrefs(r.data));
  }, [loadMe, loadAcademies]);

  const switchAcademy = async (academy_id) => {
    setBusyAcademy(academy_id); setAcademyError("");
    try {
      await api.post("/student/switch-academy", { academy_id });
      window.location.href = "/student/home";
    } catch (err) {
      setAcademyError(err?.response?.data?.detail || "Could not switch academy");
      setBusyAcademy(null);
    }
  };

  const joinAsVisitor = async (academy) => {
    if (!window.confirm(`Join ${academy.name} as a visitor? You'll be able to check in to their drop-in classes.`)) return;
    setBusyAcademy(academy.academy_id); setAcademyError("");
    try {
      await api.post("/student/join-visitor", { slug: academy.slug });
      await switchAcademy(academy.academy_id);
    } catch (err) {
      setAcademyError(err?.response?.data?.detail || "Could not join");
      setBusyAcademy(null);
    }
  };

  const onProfileSaved = (updatedStudent) => {
    setEditing(false);
    // Merge back into local state without a round trip.
    setMe(prev => prev ? { ...prev, student: updatedStudent, user: { ...prev.user, name: updatedStudent.name || prev.user.name } } : prev);
  };

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await api.put("/student/notifications", next); }
    catch (_) { setPrefs(prefs); }
  };

  const togglePush = async () => {
    setPushBusy(true); setPushError("");
    try {
      if (pushStatus === "subscribed") {
        await disablePush();
      } else {
        await enablePush();
      }
      setPushStatus(await getPushStatus());
    } catch (e) {
      setPushError(e.message || "Could not enable push");
    }
    setPushBusy(false);
  };

  if (!me) return <div className="p-8 label">Loading…</div>;
  const s = me.student;
  const isOn = pushStatus === "subscribed";
  const avatarSrc = s.avatar || user?.picture;

  return (
    <div data-testid="page-student-profile">
      <StudentSection section="Profile" title={s.name?.split(" ")[0] || "Athlete"}/>
      <div className="px-5">
        <div className="card p-5 flex items-center gap-4" data-testid="profile-header">
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className="w-14 h-14 rounded-full object-cover" data-testid="profile-avatar"/>
          ) : (
            <div className="w-14 h-14 rounded-full flex items-center justify-center text-[15px] font-semibold" style={{background:"var(--bg-elev)", border:"1px solid var(--border)"}}>{s.initials}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[16px] truncate">{s.name || user?.name}</div>
            <div className="label-tiny mt-1 truncate">{user?.email}</div>
            {s.phone && <div className="label-tiny mono mt-1" style={{color:"var(--text-dim)"}}>{s.phone}</div>}
          </div>
          <button onClick={()=>setEditing(true)} className="pill shrink-0" data-testid="btn-edit-profile"
                  style={{background:"transparent", color:"var(--green)", border:"1px solid var(--green-border)"}}>
            <Pencil size={11}/> Edit
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="card p-4 text-center"><div className="label-tiny">Total</div><div className="text-[20px] font-bold mt-1">{s.classes_count || 0}</div></div>
          <div className="card p-4 text-center"><div className="label-tiny">Last 30d</div><div className="text-[20px] font-bold mt-1">{me.last30_classes}</div></div>
          <div className="card p-4 text-center"><div className="label-tiny">Avg/mo</div><div className="text-[20px] font-bold mt-1">{s.monthly_avg || 0}</div></div>
        </div>

        <div className="card mt-4 p-4 flex items-center gap-4" data-testid="push-toggle-row">
          {isOn ? <Bell size={18} style={{color:"var(--green)"}}/> : <BellOff size={18} style={{color:"var(--text-dim)"}}/>}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[14px] uppercase tracking-tight">Push notifications</div>
            <div className="label-tiny mt-0.5" data-testid="push-status">
              {!supported ? "Not supported on this browser" :
                pushStatus === "denied" ? "Blocked — enable in browser settings" :
                isOn ? "On · You'll get class & academy alerts" :
                "Get notified when the academy posts an announcement"}
            </div>
            {pushError && <div className="label-tiny mt-1" style={{color:"var(--danger)"}} data-testid="push-error">{pushError}</div>}
          </div>
          <button onClick={togglePush} disabled={!supported || pushBusy || pushStatus === "denied"} className="pill" data-testid="push-toggle"
            style={isOn ? {background:"var(--green-bg)", color:"var(--green)", border:"1px solid var(--green-border)"} : {background:"transparent", color:"var(--text-dim)", border:"1px solid var(--border)"}}>
            {pushBusy ? "…" : isOn ? <span className="flex items-center gap-1"><Check size={11}/>On</span> : "Enable"}
          </button>
        </div>

        {/* Notification preferences */}
        {isOn && (
          <div className="card mt-4 p-4" data-testid="notif-prefs">
            <div className="label mb-3">What to notify me about</div>
            <div className="space-y-2">
              {CATS.map(({ key, label, desc, Icon }) => (
                <div key={key} className="flex items-center gap-3 py-2" data-testid={`pref-row-${key}`}>
                  <Icon size={15} style={{color: prefs[key] ? "var(--green)" : "var(--text-muted)"}}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold uppercase tracking-tight">{label}</div>
                    <div className="label-tiny mt-0.5" style={{textTransform:"none", letterSpacing:0, fontSize:11}}>{desc}</div>
                  </div>
                  <button onClick={()=>togglePref(key)} className="pill" data-testid={`pref-${key}`}
                    style={prefs[key] ? {background:"var(--green-bg)", color:"var(--green)", border:"1px solid var(--green-border)"} : {background:"transparent", color:"var(--text-dim)", border:"1px solid var(--border)"}}>
                    {prefs[key] ? "On" : "Off"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4"><LanguagePreference /></div>

        <div className="card mt-4 overflow-hidden" data-testid="academy-switcher">
          <div className="p-4" style={{borderBottom:"1px solid var(--border)"}}>
            <div className="flex items-center gap-3 mb-1">
              <Building2 size={16} style={{color:"var(--text-dim)"}}/>
              <div className="flex-1 text-[14px] font-semibold uppercase tracking-tight">My academies</div>
            </div>
            {!academies ? (
              <div className="label-tiny mt-2">Loading…</div>
            ) : (
              <div className="mt-2 space-y-2">
                <select
                  className="input"
                  value={academies.active_academy_id}
                  onChange={(e) => e.target.value !== academies.active_academy_id && switchAcademy(e.target.value)}
                  disabled={!!busyAcademy}
                  data-testid="academy-select"
                >
                  {academies.joined.map(a => (
                    <option key={a.academy_id} value={a.academy_id}>
                      {a.academy_name} — {a.academy_id === academies.home_academy_id ? "Home" : "Visitor"} · {a.status}
                    </option>
                  ))}
                </select>
                {busyAcademy && <div className="label-tiny" style={{color:"var(--text-dim)"}}>Switching…</div>}
                {academyError && <div className="label-tiny" style={{color:"var(--danger)"}} data-testid="academy-error">{academyError}</div>}
                {academies.available.length > 0 && (
                  <button onClick={() => setShowJoin(s => !s)} className="btn btn-outline w-full mt-2" data-testid="btn-toggle-join-academy">
                    <Plus size={13}/> Join another RollOSS academy
                  </button>
                )}
                {showJoin && (
                  <div className="space-y-2 mt-2" data-testid="join-academy-list">
                    {academies.available.map(a => (
                      <div key={a.academy_id} className="flex items-center gap-3 py-1" data-testid={`available-academy-${a.academy_id}`}>
                        <div className="flex-1 min-w-0 text-[13px] font-semibold truncate">{a.name}</div>
                        <button onClick={() => joinAsVisitor(a)} disabled={busyAcademy === a.academy_id} className="pill"
                                style={{background:"transparent", color:"var(--green)", border:"1px solid var(--green-border)"}}
                                data-testid={`join-visitor-${a.academy_id}`}>
                          {busyAcademy === a.academy_id ? "Joining…" : "Join as visitor"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <Row icon={HelpCircle} label="Help & support" value="" />
        </div>

        <button onClick={logout} className="btn btn-outline w-full mt-6" data-testid="student-signout">
          <LogOut size={14}/> Sign out
        </button>

        <div className="text-center mt-8">
          <div className="label-tiny">RollOSS · v1.0</div>
          <div className="label-tiny mt-1">The OS for Jiu-Jitsu</div>
        </div>
      </div>
      {editing && (
        <StudentProfileEditModal
          initial={s}
          googlePicture={user?.picture}
          onClose={()=>setEditing(false)}
          onSaved={onProfileSaved}
        />
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-4 p-4" style={{borderBottom:"1px solid var(--border)"}}>
      <Icon size={16} style={{color:"var(--text-dim)"}}/>
      <div className="flex-1 text-[14px] font-semibold uppercase tracking-tight">{label}</div>
      <div className="label-tiny">{value}</div>
      <ChevronRight size={14} style={{color:"var(--text-muted)"}}/>
    </div>
  );
}
